import * as angular from "angular";
import { Feature } from "../../models/feature.model";
import { ToastUtils } from "../../helper/toast.helper";
import {
    PermissionLevel,
    permissionLevelsConstants,
} from "../../utils/constants/permission-levels.constants";
import { PermissionCheck } from "../../shared/permission";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";

interface PermissionEntry {
    id: number;
    internalName: string;
    name: string;
    description: string;
}

const CAMPAIGN_PERMISSION_IDS = [2, 3, 4];
const ORG_PERMISSION_IDS = [3, 4, 8, 9, 10];
const MOBILE_APP_PERMISSION_IDS = [5, 6];

let PermissionLevelSettingsComponent: any = {
    selector: "vcPermissionLevelSettings",
    templateUrl:
        "/angular/components/permission-level/permission-level-settings.component.html",
    bindings: {
        feature: "<",
    },
    controller: class PermissionLevelSettingsController {
        public feature: Feature;
        public webPermissions: PermissionEntry[] = [];
        public mobilePermissions: PermissionEntry[] = [];
        public canEdit: boolean;

        private readonly toast: ToastUtils;
        private readonly permissionProvider: PermissionCheck;
        public readonly isOrganisationCampaign: boolean;
        public readonly isLeaderView: boolean;

        constructor(
            ToastUtils: ToastUtils,
            PermissionCheck: PermissionCheck,
            CampaignDataProvider: CampaignDataProvider,
            __app_type: string
        ) {
            this.toast = ToastUtils;
            this.permissionProvider = PermissionCheck;
            this.canEdit = PermissionCheck.hasPermission("PERMISSIONS");
            this.isOrganisationCampaign =
                CampaignDataProvider.activeCampaign.isOrganisationCampaign();
            this.isLeaderView = __app_type === "LEADER";
        }

        $onChanges(changes: any): void {
            if (changes.feature && this.feature) {
                this.buildPermissionLists();
            }
        }

        private buildPermissionLists(): void {
            const extra: { id: number; name: string; description: string }[] =
                this.feature.attributes.extra || [];

            const allPermissions: PermissionEntry[] =
                permissionLevelsConstants.levels.map(
                    (level: PermissionLevel): PermissionEntry => {
                        const custom = extra.find(
                            (e: PermissionEntry): boolean =>
                                e.id === level.permissionid
                        );
                        return {
                            id: level.permissionid,
                            internalName: level.internalName,
                            name: custom ? custom.name : level.permissionname,
                            description: custom
                                ? custom.description
                                : level.permissionexplanation,
                        };
                    }
                );

            const webPermissionIds = this.isOrganisationCampaign
                ? ORG_PERMISSION_IDS
                : CAMPAIGN_PERMISSION_IDS;

            this.webPermissions = allPermissions.filter(
                (p: PermissionEntry): boolean => webPermissionIds.includes(p.id)
            );
            this.mobilePermissions = allPermissions.filter(
                (p: PermissionEntry): boolean =>
                    MOBILE_APP_PERMISSION_IDS.includes(p.id)
            );
        }

        save(): void {
            const allPermissions: PermissionEntry[] = [
                ...this.webPermissions,
                ...this.mobilePermissions,
            ];

            // Validate: no empty names
            const hasEmpty: boolean = allPermissions.some(
                (p: PermissionEntry): boolean => !p.name || p.name.trim() === ""
            );
            if (hasEmpty) {
                this.toast.error("Permission names cannot be empty");
                return;
            }

            // Validate: unique names (case-insensitive)
            const names: string[] = allPermissions.map(
                (p: PermissionEntry): string => p.name.trim().toLowerCase()
            );
            const hasDuplicates: boolean = names.some(
                (name: string, i: number): boolean => names.indexOf(name) !== i
            );
            if (hasDuplicates) {
                this.toast.error("Permission names must be unique");
                return;
            }

            this.feature.attributes.extra = allPermissions.map(
                (p: PermissionEntry) => ({
                    id: p.id,
                    name: p.name.trim(),
                    description: p.description,
                })
            );

            this.feature.update().then(
                () => {
                    this.toast.success("Permission levels saved");
                    const extra = this.feature.attributes.extra;
                    this.permissionProvider.levels.forEach(
                        (level: PermissionLevel): void => {
                            const custom = extra.find(
                                (e: any): boolean => e.id === level.permissionid
                            );
                            if (custom) {
                                level.permissionname = custom.name;
                                level.permissionexplanation =
                                    custom.description;
                            }
                        }
                    );
                },
                () => this.toast.error()
            );
        }
    },
};

angular
    .module("permissionLevel", [])
    .component(
        PermissionLevelSettingsComponent.selector,
        PermissionLevelSettingsComponent
    );
